import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, XIcon, AlertTriangleIcon, LightbulbIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AUDIT_GROUP_LABELS, type AuditFinding, type AuditGroup } from '../../utils/aiAssist';
interface AiReviewPanelProps {
  open: boolean;
  loading: boolean;
  findings: AuditFinding[];
  onClose: () => void;
}
const GROUP_ORDER: AuditGroup[] = ['coherence', 'missing', 'audience', 'variables', 'languages'];

/** Full-notice review. Advisory only — it never prevents sending. */
export function AiReviewPanel({
  open,
  loading,
  findings,
  onClose
}: AiReviewPanelProps) {
  const {
    t,
    lang
  } = useLanguage();
  const warnings = findings.filter(f => f.level === 'warning').length;
  const infos = findings.filter(f => f.level === 'info').length;
  return <AnimatePresence data-id="element-3610" data-mp="ycn6eu3wpxtu">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[1200] flex justify-end bg-slate-900/30 backdrop-blur-sm" onClick={onClose} data-id="element-3611" data-mp="3h2ggjzd2008l">
          <motion.aside initial={{
        x: 40,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 40,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t("Vérification IA de l'avis", 'AI review of the notice')} className="w-full max-w-md h-full bg-white shadow-xl flex flex-col" data-id="element-3612" data-mp="3pwbkx56cpmn6">
            <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100" data-id="element-3613" data-mp="8nyc07mbn2tq">
              <div className="min-w-0" data-id="element-3614" data-mp="2eg3ntj9ov3iw">
                <h2 className="text-base font-bold text-[#0F172A] flex items-center gap-2" data-id="element-3615" data-mp="2ae0467cv8smj">
                  <SparklesIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3616" data-mp="dddpn7163all" />
                  {t("Vérification IA de l'avis", 'AI review of the notice')}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-3617" data-mp="icp94nsx4u6v">
                  {t('Recommandations seulement — l’envoi n’est jamais bloqué.', 'Recommendations only — sending is never blocked.')}
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label={t('Fermer', 'Close')} className="p-1.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-id="element-3618" data-mp="wbou9lh82qya">
                <XIcon className="w-4 h-4" data-id="element-3619" data-mp="1b1e21zjtlt7j" />
              </button>
            </header>

            {loading ? <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-400" data-id="element-3620" data-mp="1g1ii5ogtglx8">
                <Loader2Icon className="w-6 h-6 animate-spin text-[#4F46E5]" data-id="element-3621" data-mp="1k2lzygifcs13" />
                <p className="text-sm" data-id="element-3622" data-mp="34k3h1lfwogqz">
                  {t("Analyse de l'avis…", 'Analyzing the notice…')}
                </p>
              </div> : <>
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-4 flex-wrap" data-id="element-3623" data-mp="2vin69cl6icm8">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700" data-id="element-3624" data-mp="2ql3uqkfv8dxz">
                    <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-3625" data-mp="3o6j1ecv3nwgw" />
                    {warnings} {t('à vérifier', 'to check')}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4F46E5]" data-id="element-3626" data-mp="3ei15oa1ogfk4">
                    <LightbulbIcon className="w-3.5 h-3.5" data-id="element-3627" data-mp="39j7r0eanmiid" />
                    {infos} {t('suggestion(s)', 'suggestion(s)')}
                  </span>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" data-id="element-3628" data-mp="av0vgnigqkh9">
                  {GROUP_ORDER.map(group => {
              const items = findings.filter(f => f.group === group);
              if (items.length === 0) return null;
              return <section key={group} data-id="element-3629" data-mp="3qsigr58k5f5g">
                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-3630" data-mp="3crxvm2f6778s">
                          {AUDIT_GROUP_LABELS[group][lang]}
                        </h3>
                        <ul className="space-y-2" data-id="element-3631" data-mp="9a5xs7ecrt2o">
                          {items.map(finding => {
                    const Icon = finding.level === 'warning' ? AlertTriangleIcon : finding.level === 'ok' ? CheckCircle2Icon : LightbulbIcon;
                    return <li key={finding.id} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${finding.level === 'warning' ? 'border-amber-200 bg-amber-50/60' : finding.level === 'ok' ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-white'}`} data-id="element-3632" data-mp="s88tf51eoo9g">
                                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${finding.level === 'warning' ? 'text-amber-500' : finding.level === 'ok' ? 'text-emerald-600' : 'text-[#4F46E5]'}`} data-id="element-3633" data-mp="1ys371a0rytul" />
                                <p className="text-sm text-slate-700 leading-snug" data-id="element-3634" data-mp="3e81vp1usfx4g">
                                  {finding.message[lang]}
                                </p>
                              </li>;
                  })}
                        </ul>
                      </section>;
            })}
                </div>

                <footer className="px-5 py-4 border-t border-slate-100 bg-slate-50" data-id="element-3635" data-mp="2ptncdzlaotui">
                  <button type="button" onClick={onClose} className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-[#0F172A] rounded-lg hover:bg-[#1E293B] transition-colors" data-id="element-3636" data-mp="1t5pdmkm2w6hx">
                    {t('Retourner à l’avis', 'Back to the notice')}
                  </button>
                </footer>
              </>}
          </motion.aside>
        </motion.div>}
    </AnimatePresence>;
}